// ── What the chat just touched (chat Figma rebuild WP3, gap S8) ────────────────────────────────
//
// Frames 08, 11, 12 and 17 draw the left Ongoing list with the Ongoing the chat has just made or
// changed held at the TOP, carrying a blue dot (8px #3741F3 in a 16px #EEF0FF disc). It is news:
// it goes away when that Ongoing is opened, and it does not outlive the day or the tab.
//
// So this is a small, SESSION-scoped record (sessionStorage — a new tab starts clean), each entry
// kept 24 hours at most. An entry is a reference to an Ongoing by id when the chat knows it, else
// by the task it made (id + title) until the Ongoing that task started is known. Opening an Ongoing
// CLEARS its entry rather than deleting it, so an answer that is drawn again (going back to Home
// re-mounts the chat) cannot mark the same thing as news twice.
//
// Pure apart from the storage reads/writes at the bottom, which take the storage as an argument.

export const FRESH_KEY = 'busy.chatFresh'
export const FRESH_TTL_MS = 24 * 60 * 60 * 1000

const norm = (s) => String(s || '').trim().toLowerCase()

/** Entries still inside the 24h window, malformed ones dropped. */
export function pruneFresh(entries, now = Date.now()) {
  return (Array.isArray(entries) ? entries : []).filter((e) => e
    && (e.ongoingId || e.taskId)
    && Number.isFinite(e.at)
    && now - e.at < FRESH_TTL_MS)
}

/** Does this entry describe the same thing as `ref`? (same Ongoing, or same task) */
const sameAs = (e, ref) => (!!ref.ongoingId && e.ongoingId === String(ref.ongoingId))
  || (!!ref.taskId && e.taskId === String(ref.taskId))

/**
 * Add `{ ongoingId?, taskId?, title? }` as news — unless it is already known (news or cleared).
 * A known task that now names its Ongoing takes the id (the box creates the Ongoing a moment after
 * the task). Returns a new array.
 */
export function addFresh(entries, ref, now = Date.now()) {
  const r = ref || {}
  if (!r.ongoingId && !r.taskId) return pruneFresh(entries, now)
  const list = pruneFresh(entries, now)
  const i = list.findIndex((e) => sameAs(e, r))
  if (i >= 0) {
    const e = list[i]
    if (r.ongoingId && !e.ongoingId) {
      const next = list.slice()
      next[i] = { ...e, ongoingId: String(r.ongoingId) }
      return next
    }
    return list
  }
  return [{
    ongoingId: r.ongoingId ? String(r.ongoingId) : null,
    taskId: r.taskId ? String(r.taskId) : null,
    title: r.title ? String(r.title) : null,
    at: now,
    cleared: false,
  }, ...list]
}

/**
 * The Ongoing an entry points at in `items`: by id, else (an entry that only knows its task) the
 * task-started Ongoing with the task's title — the one the box / the BFF opened around it.
 */
export function resolveFresh(entry, items) {
  const list = Array.isArray(items) ? items : []
  if (entry.ongoingId) return list.find((o) => o && o.id === entry.ongoingId) || null
  const title = norm(entry.title)
  if (!title) return null
  return list.find((o) => o && !o.parentOngoingId && norm(o.origin) === 'task' && norm(o.title) === title) || null
}

/** The Ongoing ids that are news right now, newest first, each once. */
export function freshOngoingIds(entries, items, now = Date.now()) {
  const out = []
  for (const e of pruneFresh(entries, now)) {
    if (e.cleared) continue
    const o = resolveFresh(e, items)
    if (o && !out.includes(o.id)) out.push(o.id)
  }
  return out
}

/** Opening an Ongoing reads its news: every entry that resolves to it is cleared (kept, not removed). */
export function clearFresh(entries, ongoingId, items, now = Date.now()) {
  const id = String(ongoingId || '')
  const list = pruneFresh(entries, now)
  if (!id) return list
  return list.map((e) => {
    if (e.cleared) return e
    const hit = e.ongoingId === id || resolveFresh(e, items)?.id === id
    return hit ? { ...e, ongoingId: e.ongoingId || id, cleared: true } : e
  })
}

/**
 * The sidebar's rows with the fresh ones floated to the top (newest first) and flagged `fresh` —
 * after every other ordering rule has run, so the news is the one thing above the pin.
 */
export function floatFresh(rows, ids) {
  const list = Array.isArray(rows) ? rows : []
  const fresh = Array.isArray(ids) ? ids : []
  if (!fresh.length) return list
  const top = []
  for (const id of fresh) {
    const row = list.find((r) => r && r.id === id && !r.child)
    if (row) top.push({ ...row, fresh: true })
  }
  if (!top.length) return list
  const moved = new Set(top.map((r) => r.id))
  return [...top, ...list.filter((r) => !(r && !r.child && moved.has(r.id)))]
}

// ── Storage (session-scoped) ───────────────────────────────────────────────────────────────────

const defaultStorage = () => {
  try { return typeof window !== 'undefined' ? window.sessionStorage : undefined } catch { return undefined }
}

/** The record as stored, pruned — an unreadable / blocked storage is simply "nothing". */
export function readFresh(storage = defaultStorage(), now = Date.now()) {
  try {
    const raw = storage?.getItem?.(FRESH_KEY)
    return raw ? pruneFresh(JSON.parse(raw), now) : []
  } catch {
    return []
  }
}

/** Store the record. Never throws (a full or blocked storage only loses the news). */
export function writeFresh(entries, storage = defaultStorage()) {
  try { storage?.setItem?.(FRESH_KEY, JSON.stringify(Array.isArray(entries) ? entries : [])) } catch { /* ignore */ }
}
